<template>
  <section class="crm-container">
    <!-- Header with controls -->
    <div class="crm-header grid-flex middle between wrap">
      <div class="flex middle">
        <div>
          <h1 class="crm-title">CRM</h1>
          <p class="crm-subtitle">Gerencie seus pipelines, negociações e contatos de forma ágil e visual.</p>
        </div>
      </div>
      
      <div class="crm-controls flex middle gap-m margin-top-mobile">
        <!-- Pipeline Selector -->
        <div v-if="pipelines.length > 0" class="select-wrapper">
          <select v-model="selectedPipelineId" @change="changePipeline" class="pipeline-select">
            <option v-for="p in pipelines" :key="p.id" :value="p.id">
              {{ p.name }}
            </option>
          </select>
          <i class="fas fa-chevron-down select-chevron"></i>
        </div>

        <button @click="openCreatePipelineModal" class="btn btn-secondary">
          <i class="fas fa-plus"></i> Novo Funil
        </button>

        <button v-if="activePipeline" @click="openEditPipelineModal" class="btn btn-secondary" title="Configurar Funil">
          <i class="fas fa-cog"></i> Configurar Funil
        </button>

        <button v-if="activePipeline" @click="openCreateDealModal(null)" class="btn btn-primary">
          <i class="fas fa-dollar-sign"></i> Novo Negócio
        </button>
      </div>
    </div>

    <div v-if="loading" class="margin-top-l">
      <content-loading>
        <fake-line height="30"></fake-line>
        <fake-line height="30"></fake-line>
        <fake-line height="30"></fake-line>
        <fake-line height="30"></fake-line>
      </content-loading>
    </div>

    <!-- Active Pipeline Area -->
    <div v-else-if="activePipeline" class="kanban-board">
      <div class="steps-wrapper">
        <!-- Loop Steps Columns -->
        <div 
          v-for="(step, index) in activePipeline.steps" 
          :key="step.id" 
          class="step-column"
          :class="{ 'drag-over': dragOverStepId === step.id }"
          @dragover.prevent
          @dragenter="onDragEnter(step)"
          @dragleave="onDragLeave(step)"
          @drop="onDrop(step)"
        >
          <!-- Column Header -->
          <div class="step-header">
            <div class="step-meta flex middle between">
              <!-- Edit step name inline or show it -->
              <div v-if="editingStepId === step.id" class="step-name-edit flex middle gap-s">
                <input 
                  v-model="editingStepName" 
                  ref="stepNameInput"
                  @blur="saveStepName(step)" 
                  @keyup.enter="saveStepName(step)"
                  @keyup.esc="cancelStepEdit"
                  class="step-name-input"
                />
                <button @click="saveStepName(step)" class="btn-icon text-green">
                  <i class="fas fa-check"></i>
                </button>
              </div>
              <div v-else class="step-title-wrapper flex middle gap-s">
                <span class="step-title" @dblclick="startStepEdit(step)">{{ step.name }}</span>
                <button @click="startStepEdit(step)" class="btn-icon hover-show">
                  <i class="fas fa-pen fa-xs"></i>
                </button>
              </div>

              <!-- Step Actions -->
              <div class="step-actions flex gap-s">
                <!-- Configure Column -->
                <button 
                  @click="openEditStepModal(step)" 
                  class="btn-icon text-blue hover-show" 
                  title="Configurar Coluna"
                >
                  <i class="fas fa-cog fa-xs"></i>
                </button>

                <!-- Move Left -->
                <button 
                  v-if="index > 0" 
                  @click="moveStep(step, 'left')" 
                  class="btn-icon hover-show" 
                  title="Mover para Esquerda"
                >
                  <i class="fas fa-chevron-left fa-xs"></i>
                </button>

                <!-- Move Right -->
                <button 
                  v-if="index < activePipeline.steps.length - 1" 
                  @click="moveStep(step, 'right')" 
                  class="btn-icon hover-show" 
                  title="Mover para Direita"
                >
                  <i class="fas fa-chevron-right fa-xs"></i>
                </button>

                <!-- Delete Column -->
                <button @click="deleteStepConfirm(step)" class="btn-icon text-red hover-show" title="Excluir Coluna">
                  <i class="fas fa-trash-alt fa-xs"></i>
                </button>
              </div>
            </div>

            <!-- Number of Deals -->
            <div class="step-count">
              {{ step.total_deals }} {{ step.total_deals === 1 ? 'negócio' : 'negócios' }}
            </div>

            <!-- Summed Value of Deals in Step -->
            <div class="step-total">
              R$ {{ formatMoney(step.summed_value) }}
            </div>
          </div>

          <!-- Cards Scrollable Area -->
          <div class="step-cards" @scroll="handleStepScroll($event, step)">
            <!-- Loop Cards -->
            <div 
              v-for="deal in step.deals" 
              :key="deal.id" 
              class="deal-card"
              draggable="true"
              @dragstart="onDragStart(deal, step)"
              @dragend="onDragEnd"
              @click="openDealDetailsModal(deal)"
            >
              <div class="deal-card-header flex middle between">
                <h4 class="deal-card-title">{{ deal.title }}</h4>
                <div class="deal-card-actions flex gap-s">
                  <button @click.stop="openEditDealModal(deal)" class="btn-card-action text-purple" title="Editar">
                    <i class="fas fa-pen"></i>
                  </button>
                  <button @click.stop="deleteDealConfirm(deal, step)" class="btn-card-action text-red" title="Excluir">
                    <i class="fas fa-trash-alt"></i>
                  </button>
                </div>
              </div>

              <!-- Contact Info -->
              <div v-if="deal.contact" class="deal-contact-info">
                <div class="contact-name">
                  <i class="fas fa-user contact-icon"></i>
                  {{ deal.contact.first_name || '' }} {{ deal.contact.last_name || '' }}
                </div>
                <div v-if="deal.contact.email" class="contact-detail">
                  <i class="fas fa-envelope contact-icon"></i>
                  {{ deal.contact.email }}
                </div>
                <div v-if="deal.contact.phone" class="contact-detail">
                  <i class="fas fa-phone contact-icon"></i>
                  {{ deal.contact.phone }}
                </div>
                <div v-if="deal.contact.tags && deal.contact.tags.length > 0" class="flex wrap gap-xs margin-top-xs">
                  <span v-for="tag in deal.contact.tags" :key="tag.id" style="background: #edf2f7; color: #4a5568; padding: 2px 6px; border-radius: 4px; font-size: 0.7rem; border: 1px solid #e2e8f0;">
                    <i class="fas fa-tag" style="margin-right: 3px; font-size: 0.65rem;"></i>{{ tag.name }}
                  </span>
                </div>
              </div>

              <!-- Time in Funnel (Deal Age) -->
              <div class="deal-card-age flex middle gap-xs margin-top-xs" style="font-size: 0.8rem; color: #718096; margin-bottom: 2px;">
                <span>No funil há {{ getDealAge(deal.created_at) }}</span>
              </div>

              <!-- Value of the Deal and Assignee Badge -->
              <div class="deal-card-footer flex middle between margin-top-s">
                <div class="deal-value text-red font-bold" style="font-size: 1.05rem;">
                  R$ {{ formatMoney(deal.value) }}
                </div>
                <div v-if="deal.user" class="deal-assignee-badge flex middle gap-xs" title="Responsável pelo negócio">
                  <i class="far fa-user text-red" style="font-size: 0.85rem;"></i>
                  <span class="assignee-name">{{ deal.user.name }}</span>
                </div>
              </div>
            </div>

            <!-- Empty card helper inside step -->
            <div v-if="step.deals.length === 0 && !step.loading" class="step-empty-state">
              Arraste cartões para cá ou clique abaixo para criar.
            </div>

            <!-- Loading Indicator -->
            <div v-if="step.loading" class="step-loading-indicator">
              <i class="fas fa-spinner fa-spin text-purple"></i>
              <span>Carregando...</span>
            </div>
          </div>

          <!-- Add Deal to Step Button -->
          <button @click="openCreateDealModal(step.id)" class="step-add-card-btn">
            <i class="fas fa-plus"></i> Adicionar Negócio
          </button>
        </div>

        <!-- Add Step Column -->
        <div class="add-step-column">
          <div v-if="addingStep" class="add-step-form">
            <input 
              v-model="newStepName" 
              placeholder="Nome da etapa..." 
              class="new-step-input"
              @keyup.enter="createNewStep"
              ref="newStepInput"
            />
            <div class="flex gap-s margin-top-s">
              <button @click="createNewStep" class="btn btn-primary small btn-block">Salvar</button>
              <button @click="addingStep = false" class="btn btn-secondary small">Cancelar</button>
            </div>
          </div>
          <button v-else @click="startAddStep" class="add-step-btn">
            <i class="fas fa-plus"></i> Adicionar Coluna
          </button>
        </div>
      </div>
    </div>

    <!-- Empty/Initial State -->
    <div v-else class="crm-empty-state">
      <div class="empty-icon-wrapper">
        <i class="fas fa-funnel-dollar empty-icon"></i>
      </div>
      <h2>Nenhum funil de vendas ativo</h2>
      <p>Crie o seu primeiro pipeline para começar a gerenciar contatos e fechar negócios!</p>
      <button @click="openCreatePipelineModal" class="btn btn-primary margin-top-m">
        <i class="fas fa-plus"></i> Criar Primeiro Funil
      </button>
    </div>

    <!-- MODAL: Create Pipeline -->
    <div v-if="showCreatePipelineModal" class="modal-overlay">
      <div class="modal-card">
        <div class="modal-header">
          <h3>Criar Novo Funil de Vendas</h3>
          <button @click="closeCreatePipelineModal" class="modal-close-btn">&times;</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>Nome do Pipeline</label>
            <input 
              v-model="newPipelineName" 
              type="text" 
              placeholder="Ex: Vendas Online, Parcerias, etc."
              class="form-control"
              @keyup.enter="submitCreatePipeline"
            />
          </div>
          <p class="modal-hint">
            <strong>Dica:</strong> Ao criar um funil, criamos automaticamente 4 etapas iniciais para você (Contato, Qualificação, Negociação, Fechamento).
          </p>
        </div>
        <div class="modal-footer flex right gap-m">
          <button @click="closeCreatePipelineModal" class="btn btn-secondary">Cancelar</button>
          <button @click="submitCreatePipeline" class="btn btn-primary">Criar Funil</button>
        </div>
      </div>
    </div>
    <!-- MODAL: Edit/Configure Pipeline -->
    <div v-if="showEditPipelineModal" class="modal-overlay">
      <div class="modal-card">
        <div class="modal-header">
          <h3>Configurar Funil de Vendas</h3>
          <button @click="closeEditPipelineModal" class="modal-close-btn">&times;</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>Nome do Funil</label>
            <input 
              v-model="editPipelineName" 
              type="text" 
              placeholder="Ex: Novo Nome do Funil..."
              class="form-control"
              @keyup.enter="submitEditPipeline"
            />
          </div>
        </div>
        <div class="modal-footer flex middle between">
          <button @click="deletePipelineConfirm" class="btn btn-danger small">
            <i class="fas fa-trash-alt"></i> Excluir Funil
          </button>
          
          <div class="flex gap-m">
            <button @click="closeEditPipelineModal" class="btn btn-secondary small">Cancelar</button>
            <button @click="submitEditPipeline" class="btn btn-primary small">Salvar Alterações</button>
          </div>
        </div>
      </div>
    </div>

    <!-- MODAL: Edit/Configure Step -->
    <div v-if="showEditStepModal" class="modal-overlay">
      <div class="modal-card">
        <div class="modal-header">
          <h3>Configurar Coluna</h3>
          <button @click="showEditStepModal = false" class="modal-close-btn">&times;</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>Nome da Coluna</label>
            <input 
              v-model="editStepForm.name" 
              type="text" 
              class="form-control"
              @keyup.enter="submitEditStep"
            />
          </div>
          <div class="form-group">
            <label>Tag Associada (Automação)</label>
            <p class="modal-hint" style="margin-top: -5px; margin-bottom: 10px;">
              Ao associar uma Tag aqui, negócios (Deals) poderão ser movidos automaticamente para esta coluna quando o contato receber esta Tag.
            </p>
            <select v-model="editStepForm.contact_tag_id" class="form-control">
              <option :value="null">Nenhuma (Desativado)</option>
              <option v-for="t in tags" :key="t.id" :value="t.id">
                {{ t.name }}
              </option>
            </select>
          </div>
        </div>
        <div class="modal-footer flex right gap-m">
          <button @click="showEditStepModal = false" class="btn btn-secondary">Cancelar</button>
          <button @click="submitEditStep" class="btn btn-primary">Salvar Alterações</button>
        </div>
      </div>
    </div>

    <!-- MODAL: Create/Edit Deal -->
    <div v-if="showDealModal" class="modal-overlay">
      <div class="modal-card">
        <div class="modal-header">
          <h3>{{ isEditingDeal ? 'Editar Negócio' : 'Criar Novo Negócio' }}</h3>
          <button @click="closeDealModal" class="modal-close-btn">&times;</button>
        </div>
        <div class="modal-body">
          <!-- Contact Autocomplete Lookup (Only when creating) -->
          <div v-if="!isEditingDeal" class="form-group relative">
            <div class="flex middle between margin-bottom-xs">
              <label style="margin-bottom: 0;">Contato <span class="text-red">*</span></label>
              <button 
                type="button" 
                @click="createContactMode = !createContactMode" 
                class="btn btn-secondary small"
                style="padding: 4px 10px; font-size: 1.2rem; font-weight: 600; background: #f7fafc; border-color: #cbd5e0; color: #4a5568; margin-bottom: 4px; border-radius: 6px;"
              >
                {{ createContactMode ? '🔍 Buscar Existente' : '➕ Novo Contato' }}
              </button>
            </div>

            <!-- Mode 1: Search Existing Contact -->
            <div v-if="!createContactMode">
              <div class="search-input-wrapper">
                <input 
                  v-model="contactSearchQuery" 
                  type="text" 
                  placeholder="Busque por nome ou email..."
                  class="form-control"
                  @input="onContactSearchInput"
                />
                <i class="fas fa-search search-icon"></i>
              </div>
              
              <!-- Matches Dropdown -->
              <ul v-if="matchingContacts.length > 0" class="autocomplete-list">
                <li 
                  v-for="c in matchingContacts" 
                  :key="c.id" 
                  @click="selectContactForDeal(c)"
                >
                  <strong>{{ c.first_name || '' }} {{ c.last_name || '' }}</strong> - {{ c.email }}
                </li>
              </ul>

              <!-- Selected Contact Box -->
              <div v-if="selectedContact" class="selected-contact-pill flex middle between">
                <div>
                  <i class="fas fa-check-circle text-green margin-right-s"></i>
                  <strong>{{ selectedContact.first_name }} {{ selectedContact.last_name }}</strong> ({{ selectedContact.email }})
                </div>
                <button @click="clearSelectedContact" class="btn-icon text-red">
                  <i class="fas fa-times"></i>
                </button>
              </div>
            </div>

            <!-- Mode 2: Quick Create New Contact -->
            <div v-else class="new-contact-inline-form flex column gap-m padding-m" style="background: rgba(125, 7, 7, 0.02); border: 1.5px dashed rgba(125, 7, 7, 0.15); border-radius: 8px; margin-top: 5px;">
              <div class="flex gap-m">
                <div class="form-group flex-1" style="margin-bottom: 0;">
                  <label style="font-size: 1.3rem; font-weight: 700; color: #2d3748; margin-bottom: 6px; display: block;">Primeiro Nome <span class="text-red">*</span></label>
                  <input v-model="newContactForm.first_name" type="text" placeholder="Ex: João" class="form-control" />
                </div>
                <div class="form-group flex-1" style="margin-bottom: 0;">
                  <label style="font-size: 1.3rem; font-weight: 700; color: #2d3748; margin-bottom: 6px; display: block;">Sobrenome</label>
                  <input v-model="newContactForm.last_name" type="text" placeholder="Ex: Silva" class="form-control" />
                </div>
              </div>
              <div class="form-group" style="margin-bottom: 0;">
                <label style="font-size: 1.3rem; font-weight: 700; color: #2d3748; margin-bottom: 6px; display: block;">E-mail <span class="text-red">*</span></label>
                <input v-model="newContactForm.email" type="email" placeholder="Ex: joao@email.com" class="form-control" />
              </div>
              <div class="form-group" style="margin-bottom: 0;">
                <label style="font-size: 1.3rem; font-weight: 700; color: #2d3748; margin-bottom: 6px; display: block;">Telefone (com DDD)</label>
                <input v-model="newContactForm.phone" type="text" placeholder="Ex: 11999999999" class="form-control" />
              </div>
            </div>
          </div>

          <!-- Title -->
          <div class="form-group">
            <label>Título do Negócio <span class="text-red">*</span></label>
            <input 
              v-model="dealForm.title" 
              type="text" 
              placeholder="Ex: Licença Enterprise - João Silva"
              class="form-control"
            />
          </div>

          <!-- Value -->
          <div class="form-group">
            <label>Valor Comercial</label>
            <money 
              v-model="dealForm.value" 
              v-bind="moneyConfig"
              class="form-control"
            ></money>
          </div>

          <!-- Step selection (Only if creating without step_id preset or editing) -->
          <div class="form-group">
            <label>Etapa do Funil</label>
            <select v-model="dealForm.step_id" class="form-control">
              <option 
                v-for="step in activePipeline.steps" 
                :key="step.id" 
                :value="step.id"
              >
                {{ step.name }}
              </option>
            </select>
          </div>

          <!-- Responsável pelo Negócio selection -->
          <div class="form-group">
            <label>Responsável pelo Negócio</label>
            <select v-model="dealForm.user_id" class="form-control">
              <option :value="null">Nenhum responsável</option>
              <option 
                v-for="u in pipelineUsers" 
                :key="u.id" 
                :value="u.id"
              >
                {{ u.name }} ({{ u.email }})
              </option>
            </select>
          </div>
        </div>
        <div class="modal-footer flex right gap-m">
          <button @click="closeDealModal" class="btn btn-secondary">Cancelar</button>
          <button @click="submitDealForm" class="btn btn-primary">
            {{ isEditingDeal ? 'Salvar Alterações' : 'Criar Negócio' }}
          </button>
        </div>
      </div>
    </div>

    <!-- MODAL: Deal Details & Interaction Center (HubSpot Premium Style) -->
    <div v-if="showDealDetailsModal && selectedDetailedDeal" class="modal-overlay" @click.self="closeDealDetailsModal">
      <div class="modal-card deal-details-modal">
        <!-- Header -->
        <div class="modal-header flex middle between">
          <div class="flex middle gap-m">
            <div class="deal-status-badge">
              <i class="fas fa-handshake"></i>
            </div>
            <div>
              <h3 class="modal-title">{{ selectedDetailedDeal.title }}</h3>
              <p class="modal-subtitle flex middle gap-s" style="font-size: 0.95rem; color: #718096; margin-top: 4px;">
                <span><i class="far fa-calendar-alt"></i> Criado em {{ formatDate(selectedDetailedDeal.created_at) }}</span>
                <span style="color: #cbd5e0;">|</span>
                <span class="text-red font-bold"><i class="far fa-clock"></i> No funil há {{ getDealAge(selectedDetailedDeal.created_at) }}</span>
              </p>
            </div>
          </div>
          <button @click="closeDealDetailsModal" class="modal-close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <!-- Two Column Content Grid -->
        <div class="modal-body deal-details-body-grid">
          
          <!-- LEFT COLUMN: Timeline & Comments (60% width) -->
          <div class="deal-timeline-panel flex column gap-m">
            
            <!-- Assigne / Responsável Card (Moved to Left Column above History) -->
            <div class="timeline-assignee-card flex middle between padding-s" style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; margin-bottom: 5px;">
              <div class="flex middle gap-s">
                <i class="far fa-user text-red" style="font-size: 1.15rem;"></i>
                <span style="font-size: 1.05rem; font-weight: 700; color: #4a5568;">Responsável pelo Negócio:</span>
              </div>
              <div class="assignee-select-wrapper" style="width: 250px;">
                <select 
                  :value="selectedDetailedDeal.user ? selectedDetailedDeal.user.id : ''" 
                  @change="onDetailedDealUserChange"
                  class="form-control assignee-dropdown"
                  style="font-size: 1.05rem; font-weight: 600; padding: 6px 12px; border-radius: 8px; width: 100%; border: 1.5px solid #cbd5e0; background: #ffffff; color: #2d3748;"
                >
                  <option value="">Sem responsável</option>
                  <option 
                    v-for="u in pipelineUsers" 
                    :key="u.id" 
                    :value="u.id"
                  >
                    {{ u.name }}
                  </option>
                </select>
              </div>
            </div>

            <div class="section-header flex middle between">
              <h4 class="section-title">
                <i class="far fa-comments"></i> Histórico & Notas
              </h4>
              <span class="badge-count" v-if="selectedDetailedDeal.comments">
                {{ selectedDetailedDeal.comments.length }} {{ selectedDetailedDeal.comments.length === 1 ? 'nota' : 'notas' }}
              </span>
            </div>

            <!-- Comment Feed -->
            <div class="comments-feed-container">
              <div v-if="!selectedDetailedDeal.comments || selectedDetailedDeal.comments.length === 0" class="empty-feed-state">
                <div class="empty-icon-circle">
                  <i class="far fa-sticky-note"></i>
                </div>
                <h5>Sem anotações ainda</h5>
                <p>Use o campo abaixo para registrar ligações, e-mails ou anotações internas sobre a evolução deste negócio.</p>
              </div>
              
              <div v-else class="timeline-feed">
                <div 
                  v-for="(c, idx) in selectedDetailedDeal.comments" 
                  :key="idx" 
                  class="timeline-item"
                >
                  <div class="timeline-badge">
                    <i class="fas fa-user"></i>
                  </div>
                  <div class="timeline-content-card">
                    <div class="timeline-card-header flex middle between">
                      <span class="comment-author">{{ c.author || 'Membro da Equipe' }}</span>
                      <span class="comment-date">{{ formatDate(c.created_at) }}</span>
                    </div>
                    <div class="comment-text-content">{{ c.text }}</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Add Comment Form -->
            <div class="add-note-box flex column gap-s">
              <h5 class="input-title">Nova Anotação / Comentário</h5>
              <div class="textarea-wrapper">
                <textarea 
                  v-model="newCommentText" 
                  placeholder="Registre o que foi conversado ou observações importantes..." 
                  class="comment-textarea"
                  rows="4"
                  cols="50"
                ></textarea>
                <i class="far fa-edit textarea-icon"></i>
              </div>
              <div class="flex right">
                <button 
                  @click="addCommentToDeal" 
                  class="btn-add-comment flex middle center gap-xs"
                  :disabled="!newCommentText.trim()"
                >
                  <i class="fas fa-plus"></i> Salvar Comentário
                </button>
              </div>
            </div>
          </div>

          <!-- RIGHT COLUMN: Contact & Financial Details (40% width) -->
          <div class="deal-info-sidebar flex column gap-l">
            
            <!-- Value Highlight -->
            <div class="sidebar-value-card">
              <span class="sidebar-label">VALOR ESTIMADO</span>
              <div class="sidebar-value text-red">R$ {{ formatMoney(selectedDetailedDeal.value) }}</div>
            </div>

            <!-- Contact Information Card -->
            <div class="sidebar-contact-card" v-if="selectedDetailedDeal.contact">
              <h4 class="section-title">
                <i class="far fa-user"></i> Dados do Contato
              </h4>
              
              <div class="contact-details-list flex column gap-s">
                <div class="contact-detail-row">
                  <span class="row-label">Nome: </span>
                  <span class="row-value font-bold">{{ selectedDetailedDeal.contact.first_name || '' }} {{ selectedDetailedDeal.contact.last_name || '' }}</span>
                </div>
                <div class="contact-detail-row" v-if="selectedDetailedDeal.contact.email">
                  <span class="row-label">E-mail: </span>
                  <span class="row-value select-all">{{ selectedDetailedDeal.contact.email }}</span>
                </div>
                <div class="contact-detail-row" v-if="selectedDetailedDeal.contact.phone">
                  <span class="row-label">Telefone: </span>
                  <span class="row-value select-all">{{ selectedDetailedDeal.contact.phone }}</span>
                </div>
                <div class="contact-detail-row" style="flex-direction: column; align-items: flex-start;" v-if="selectedDetailedDeal.contact.tags && selectedDetailedDeal.contact.tags.length > 0">
                  <span class="row-label" style="margin-bottom: 4px;">Tags: </span>
                  <div class="flex wrap gap-xs">
                    <span v-for="tag in selectedDetailedDeal.contact.tags" :key="tag.id" style="background: #edf2f7; color: #4a5568; padding: 3px 8px; border-radius: 6px; font-size: 0.75rem; border: 1px solid #e2e8f0; display: inline-block;">
                      <i class="fas fa-tag" style="margin-right: 4px; font-size: 0.7rem;"></i>{{ tag.name }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Quick Communication Channels -->
              <div class="channels-action-grid flex column gap-s margin-top-m">
                <span class="sidebar-label">Ações Rápidas</span>
                
                <!-- WhatsApp Button -->
                <a 
                  v-if="selectedDetailedDeal.contact.phone"
                  :href="getWhatsAppLink(selectedDetailedDeal.contact.phone)" 
                  target="_blank" 
                  class="action-channel-btn whatsapp-action-btn flex middle center gap-s"
                >
                  <i class="fab fa-whatsapp"></i> Chamar no WhatsApp
                </a>
                <button 
                  v-else 
                  class="action-channel-btn disabled flex middle center gap-s" 
                  disabled
                >
                  <i class="fab fa-whatsapp"></i> Sem WhatsApp (Sem Telefone)
                </button>

                <!-- Email Button -->
                <a 
                  v-if="selectedDetailedDeal.contact.email"
                  :href="'mailto:' + selectedDetailedDeal.contact.email + '?subject=' + encodeURIComponent('Negócio: ' + selectedDetailedDeal.title)" 
                  class="action-channel-btn email-action-btn flex middle center gap-s"
                >
                  <i class="far fa-envelope"></i> Enviar E-mail
                </a>
                <button 
                  v-else 
                  class="action-channel-btn disabled flex middle center gap-s" 
                  disabled
                >
                  <i class="far fa-envelope"></i> Sem E-mail Cadastrado
                </button>
              </div>
            </div>

          </div>

        </div>
        
        <!-- Footer -->
        <div class="modal-footer flex right">
          <button @click="closeDealDetailsModal" class="btn btn-secondary-outline">Fechar</button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { 
  getPipelines, 
  getPipeline, 
  createPipeline, 
  updatePipeline, 
  deletePipeline,
  createDeal, 
  updateDeal, 
  deleteDeal,
  getPipelineUsers,
  getStepDeals
} from "@/services/pipeline";
import { getContacts, createContact } from "@/services/contact";
import { getTags } from "@/services/tag";
import { Money } from 'v-money';

export default {
  name: "PipelineCRM",
  components: {
    Money
  },
  data() {
    return {
      loading: false,
      pipelines: [],
      selectedPipelineId: null,
      pipelineUsers: [],
      activePipeline: null,
      
      // Pipelines CRUD
      showCreatePipelineModal: false,
      newPipelineName: "",
      showEditPipelineModal: false,
      editPipelineName: "",
      
      // Steps CRM
      tags: [],
      showEditStepModal: false,
      editStepForm: {
        id: null,
        name: "",
        contact_tag_id: null
      },
      editingStepId: null,
      editingStepName: "",
      addingStep: false,
      newStepName: "",

      // Deals CRM Modals
      showDealModal: false,
      showDealDetailsModal: false,
      selectedDetailedDeal: null,
      newCommentText: "",
      isEditingDeal: false,
      selectedContact: null,
      contactSearchQuery: "",
      matchingContacts: [],
      createContactMode: false,
      newContactForm: {
        first_name: "",
        last_name: "",
        email: "",
        phone: ""
      },
      dealForm: {
        id: null,
        contact_id: null,
        step_id: null,
        user_id: null,
        title: "",
        value: 0
      },
      moneyConfig: {
        decimal: ',',
        thousands: '.',
        prefix: 'R$ ',
        suffix: '',
        precision: 2,
        masked: false
      },

      // Drag and Drop active states
      draggedDeal: null,
      draggedFromStep: null,
      dragOverStepId: null,
      searchTimeout: null
    };
  },
  mounted() {
    this.loadTags();
    this.loadPipelines();
    this.loadPipelineUsers();
  },
  methods: {
    // ----------------------------------------------------
    // Load & Initialize data
    // ----------------------------------------------------
    loadTags() {
      getTags()
        .then(response => {
          this.tags = response || [];
        })
        .catch(err => {
          console.error("Erro ao carregar tags:", err);
        });
    },
    loadPipelineUsers() {
      getPipelineUsers()
        .then((response) => {
          this.pipelineUsers = response.users || [];
        })
        .catch((err) => {
          console.error("Erro ao carregar usuários do pipeline:", err);
        });
    },
    loadPipelines() {
      this.loading = true;
      getPipelines()
        .then((response) => {
          this.pipelines = response.pipelines || [];
          if (this.pipelines.length > 0) {
            // Select first pipeline by default if none selected
            if (!this.selectedPipelineId) {
              this.selectedPipelineId = this.pipelines[0].id;
            }
            this.loadActivePipeline();
          } else {
            this.activePipeline = null;
            this.loading = false;
          }
        })
        .catch((err) => {
          console.error(err);
          this.$toast.error({
            title: "Erro",
            message: "Não foi possível carregar os pipelines de vendas."
          });
          this.loading = false;
        });
    },
    loadActivePipeline() {
      if (!this.selectedPipelineId) return;
      this.loading = true;
      getPipeline(this.selectedPipelineId)
        .then((response) => {
          if (response && response.steps) {
            response.steps.forEach(step => {
              step.loading = false;
            });
          }
          this.activePipeline = response;
        })
        .catch((err) => {
          console.error(err);
          this.$toast.error({
            title: "Erro",
            message: "Não foi possível carregar o pipeline selecionado."
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    changePipeline() {
      this.loadActivePipeline();
    },

    // ----------------------------------------------------
    // Pipelines CRUD
    // ----------------------------------------------------
    openCreatePipelineModal() {
      this.newPipelineName = "";
      this.showCreatePipelineModal = true;
    },
    closeCreatePipelineModal() {
      this.showCreatePipelineModal = false;
    },
    submitCreatePipeline() {
      if (!this.newPipelineName.trim()) {
        this.$toast.error({
          title: "Validação",
          message: "O nome do pipeline é obrigatório."
        });
        return;
      }
      createPipeline({ name: this.newPipelineName })
        .then((newPipe) => {
          this.$toast.success({
            title: "Sucesso",
            message: `Funil "${newPipe.name}" criado com sucesso!`
          });
          this.selectedPipelineId = newPipe.id;
          this.showCreatePipelineModal = false;
          this.loadPipelines();
        })
        .catch((err) => {
          console.error(err);
          this.$toast.error({
            title: "Erro",
            message: "Não foi possível criar o pipeline."
          });
        });
    },
    deletePipelineConfirm() {
      if (!this.activePipeline) return;
      if (!confirm(`Tem certeza que deseja excluir permanentemente o funil "${this.activePipeline.name}" e todos os seus negócios?`)) {
        return;
      }
      deletePipeline(this.activePipeline.id)
        .then(() => {
          this.$toast.success({
            title: "Sucesso",
            message: `Funil "${this.activePipeline.name}" excluído com sucesso.`
          });
          this.selectedPipelineId = null;
          this.activePipeline = null;
          this.showEditPipelineModal = false;
          this.loadPipelines();
        })
        .catch((err) => {
          console.error(err);
          this.$toast.error({
            title: "Erro",
            message: "Não foi possível excluir o funil."
          });
        });
    },
    openEditPipelineModal() {
      if (!this.activePipeline) return;
      this.editPipelineName = this.activePipeline.name;
      this.showEditPipelineModal = true;
    },
    closeEditPipelineModal() {
      this.showEditPipelineModal = false;
      this.editPipelineName = "";
    },
    submitEditPipeline() {
      if (!this.editPipelineName.trim()) {
        this.$toast.error({
          title: "Validação",
          message: "O nome do pipeline é obrigatório."
        });
        return;
      }
      updatePipeline(this.activePipeline.id, { name: this.editPipelineName })
        .then(() => {
          this.$toast.success({
            title: "Sucesso",
            message: "Funil atualizado com sucesso!"
          });
          this.showEditPipelineModal = false;
          this.loadPipelines();
        })
        .catch((err) => {
          console.error(err);
          this.$toast.error({
            title: "Erro",
            message: "Não foi possível atualizar o pipeline."
          });
        });
    },
    // ----------------------------------------------------
    // Configure Step
    // ----------------------------------------------------
    openEditStepModal(step) {
      this.editStepForm = {
        id: step.id,
        name: step.name,
        contact_tag_id: step.contact_tag_id || null
      };
      this.showEditStepModal = true;
    },
    submitEditStep() {
      if (!this.editStepForm.name.trim()) {
        this.$toast.error({
          title: "Validação",
          message: "O nome da coluna é obrigatório."
        });
        return;
      }
      const stepIndex = this.activePipeline.steps.findIndex(s => s.id === this.editStepForm.id);
      if (stepIndex > -1) {
        const updatedSteps = [...this.activePipeline.steps];
        updatedSteps[stepIndex] = {
          ...updatedSteps[stepIndex],
          name: this.editStepForm.name,
          contact_tag_id: this.editStepForm.contact_tag_id
        };
        
        updatePipeline(this.activePipeline.id, { steps: updatedSteps })
          .then(res => {
            this.$toast.success({
              title: "Sucesso",
              message: "Coluna atualizada com sucesso!"
            });
            this.showEditStepModal = false;
            this.activePipeline = res;
            if (this.activePipeline && this.activePipeline.steps) {
              this.activePipeline.steps.forEach(step => {
                step.loading = false;
              });
            }
          })
          .catch(err => {
            console.error(err);
            this.$toast.error({
              title: "Erro",
              message: "Erro ao atualizar a coluna."
            });
          });
      }
    },

    // ----------------------------------------------------
    // Steps CRUD & Inline edits
    // ----------------------------------------------------
    startStepEdit(step) {
      this.editingStepId = step.id;
      this.editingStepName = step.name;
      this.$nextTick(() => {
        if (this.$refs.stepNameInput && this.$refs.stepNameInput[0]) {
          this.$refs.stepNameInput[0].focus();
        }
      });
    },
    cancelStepEdit() {
      this.editingStepId = null;
      this.editingStepName = "";
    },
    saveStepName(step) {
      if (!this.editingStepName.trim()) {
        this.cancelStepEdit();
        return;
      }
      const updatedSteps = this.activePipeline.steps.map(s => {
        return {
          id: s.id,
          name: s.id === step.id ? this.editingStepName : s.name,
          order: s.order
        };
      });

      updatePipeline(this.activePipeline.id, { steps: updatedSteps })
        .then(() => {
          step.name = this.editingStepName;
          this.editingStepId = null;
          this.$toast.success({
            title: "Sucesso",
            message: "Nome da etapa atualizado."
          });
        })
        .catch((err) => {
          console.error(err);
          this.$toast.error({
            title: "Erro",
            message: "Não foi possível atualizar a etapa."
          });
        });
    },
    deleteStepConfirm(step) {
      if (step.deals.length > 0) {
        if (!confirm(`Atenção: A etapa "${step.name}" possui ${step.deals.length} negócios ativos. Excluir a etapa irá desativar estes negócios. Deseja prosseguir?`)) {
          return;
        }
      } else {
        if (!confirm(`Tem certeza que deseja excluir a etapa "${step.name}"?`)) {
          return;
        }
      }

      const updatedSteps = this.activePipeline.steps.map(s => {
        return {
          id: s.id,
          name: s.name,
          order: s.order,
          status: s.id === step.id ? 'DISABLE' : 'ACTIVE'
        };
      });

      updatePipeline(this.activePipeline.id, { steps: updatedSteps })
        .then(() => {
          this.$toast.success({
            title: "Sucesso",
            message: "Etapa removida com sucesso."
          });
          this.loadActivePipeline();
        })
        .catch((err) => {
          console.error(err);
          this.$toast.error({
            title: "Erro",
            message: "Não foi possível remover a etapa."
          });
        });
    },
    startAddStep() {
      this.addingStep = true;
      this.newStepName = "";
      this.$nextTick(() => {
        if (this.$refs.newStepInput) {
          this.$refs.newStepInput.focus();
        }
      });
    },
    createNewStep() {
      if (!this.newStepName.trim()) {
        this.addingStep = false;
        return;
      }
      const maxOrder = this.activePipeline.steps.reduce((max, s) => s.order > max ? s.order : max, 0);
      const updatedSteps = [
        ...this.activePipeline.steps.map(s => ({ id: s.id, name: s.name, order: s.order })),
        { name: this.newStepName, order: maxOrder + 1 }
      ];

      updatePipeline(this.activePipeline.id, { steps: updatedSteps })
        .then(() => {
          this.$toast.success({
            title: "Sucesso",
            message: `Etapa "${this.newStepName}" criada!`
          });
          this.addingStep = false;
          this.loadActivePipeline();
        })
        .catch((err) => {
          console.error(err);
          this.$toast.error({
            title: "Erro",
            message: "Não foi possível adicionar a nova etapa."
          });
        });
    },
    moveStep(step, direction) {
      const steps = [...this.activePipeline.steps];
      const index = steps.findIndex(s => s.id === step.id);
      if (index === -1) return;

      const targetIndex = direction === 'left' ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= steps.length) return;

      const currentStep = steps[index];
      const targetStep = steps[targetIndex];

      // Swap orders
      const tempOrder = currentStep.order;
      currentStep.order = targetStep.order;
      targetStep.order = tempOrder;

      // Sort locally for instant visual feedback
      this.activePipeline.steps.sort((a, b) => a.order - b.order);

      // Persist in backend
      const updatedSteps = this.activePipeline.steps.map(s => ({
        id: s.id,
        name: s.name,
        order: s.order
      }));

      updatePipeline(this.activePipeline.id, { steps: updatedSteps })
        .then(() => {
          this.$toast.success({
            title: "Sucesso",
            message: `Coluna "${step.name}" reordenada com sucesso.`
          });
        })
        .catch((err) => {
          console.error(err);
          this.$toast.error({
            title: "Erro",
            message: "Não foi possível salvar a nova ordem das colunas."
          });
          this.loadActivePipeline();
        });
    },

    // ----------------------------------------------------
    // Deals CRUD
    // ----------------------------------------------------
    openCreateDealModal(presetStepId) {
      this.isEditingDeal = false;
      this.selectedContact = null;
      this.contactSearchQuery = "";
      this.matchingContacts = [];
      this.createContactMode = false;
      this.newContactForm = {
        first_name: "",
        last_name: "",
        email: "",
        phone: ""
      };
      this.dealForm = {
        id: null,
        contact_id: null,
        step_id: presetStepId || (this.activePipeline.steps.length > 0 ? this.activePipeline.steps[0].id : null),
        user_id: null,
        title: "",
        value: 0
      };
      this.showDealModal = true;
    },
    openEditDealModal(deal) {
      this.isEditingDeal = true;
      this.selectedContact = deal.contact;
      this.createContactMode = false;
      this.dealForm = {
        id: deal.id,
        contact_id: deal.contact ? deal.contact.id : null,
        step_id: this.activePipeline.steps.find(s => s.deals.some(d => d.id === deal.id)).id,
        user_id: deal.user ? deal.user.id : null,
        title: deal.title,
        value: deal.value
      };
      this.showDealModal = true;
    },
    closeDealModal() {
      this.showDealModal = false;
    },
    openDealDetailsModal(deal) {
      this.selectedDetailedDeal = deal;
      this.newCommentText = "";
      this.showDealDetailsModal = true;
    },
    closeDealDetailsModal() {
      this.showDealDetailsModal = false;
      this.selectedDetailedDeal = null;
      this.newCommentText = "";
    },
    onDetailedDealUserChange(event) {
      const userId = event.target.value ? parseInt(event.target.value) : null;
      const dealId = this.selectedDetailedDeal.id;
      
      updateDeal(dealId, { user_id: userId })
        .then((updatedDeal) => {
          this.selectedDetailedDeal = updatedDeal;
          this.loadActivePipeline();
          this.$toast.success({
            title: "Sucesso",
            message: "Responsável pelo negócio atualizado com sucesso."
          });
        })
        .catch((err) => {
          console.error(err);
          this.$toast.error({
            title: "Erro",
            message: "Não foi possível atualizar o responsável pelo negócio."
          });
        });
    },
    getDealAge(createdAt) {
      if (!createdAt) return 'Recente';
      try {
        const created = new Date(createdAt);
        const now = new Date();
        const diffMs = now - created;
        if (diffMs < 0) return 'Recentemente';

        const diffMinutes = Math.floor(diffMs / (1000 * 60));
        if (diffMinutes < 60) {
          return diffMinutes <= 1 ? 'Menos de 1 min' : `${diffMinutes} min`;
        }

        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        if (diffHours < 24) {
          return diffHours === 1 ? '1 hora' : `${diffHours} horas`;
        }

        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        if (diffDays < 30) {
          return diffDays === 1 ? '1 dia' : `${diffDays} dias`;
        }

        const diffMonths = Math.floor(diffDays / 30);
        if (diffMonths < 12) {
          return diffMonths === 1 ? '1 mês' : `${diffMonths} meses`;
        }

        const diffYears = Math.floor(diffMonths / 12);
        return diffYears === 1 ? '1 ano' : `${diffYears} anos`;
      } catch (e) {
        return 'Recente';
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      try {
        const date = new Date(dateStr);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${day}/${month}/${year} às ${hours}:${minutes}`;
      } catch (e) {
        return dateStr;
      }
    },
    getWhatsAppLink(phone) {
      if (!phone) return '#';
      let cleaned = phone.toString().replace(/\D/g, '');
      if (cleaned.length === 10 || cleaned.length === 11) {
        cleaned = '55' + cleaned;
      }
      return `https://wa.me/${cleaned}`;
    },
    addCommentToDeal() {
      if (!this.newCommentText.trim()) return;

      const userName = this.$store.state.user && this.$store.state.user.name ? this.$store.state.user.name : "Membro da Equipe";

      const newComment = {
        text: this.newCommentText.trim(),
        author: userName,
        created_at: new Date().toISOString()
      };

      const updatedComments = [...(this.selectedDetailedDeal.comments || []), newComment];

      updateDeal(this.selectedDetailedDeal.id, { comments: updatedComments })
        .then((response) => {
          this.selectedDetailedDeal.comments = response.comments;
          this.newCommentText = "";
          this.$toast.success({
            title: "Sucesso",
            message: "Comentário adicionado com sucesso."
          });
          
          this.loadActivePipeline();
        })
        .catch((err) => {
          console.error(err);
          this.$toast.error({
            title: "Erro",
            message: "Não foi possível adicionar o comentário."
          });
        });
    },
    onContactSearchInput() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        const query = this.contactSearchQuery;
        if (!query || query.trim().length < 2) {
          this.matchingContacts = [];
          return;
        }
        getContacts({ search: query, limit: 10 })
          .then((response) => {
            this.matchingContacts = response.contacts || [];
          })
          .catch((err) => console.error(err));
      }, 300);
    },
    selectContactForDeal(contact) {
      this.selectedContact = contact;
      this.dealForm.contact_id = contact.id;
      this.contactSearchQuery = "";
      this.matchingContacts = [];
      if (!this.dealForm.title) {
        this.dealForm.title = `Negócio - ${contact.first_name || ''} ${contact.last_name || ''}`.trim() || `Negócio com ${contact.email}`;
      }
    },
    clearSelectedContact() {
      this.selectedContact = null;
      this.dealForm.contact_id = null;
      this.dealForm.title = "";
    },
    submitDealForm() {
      if (this.isEditingDeal) {
        if (!this.dealForm.title.trim()) {
          this.$toast.error({
            title: "Validação",
            message: "O título do negócio é obrigatório."
          });
          return;
        }
        // Edit flow
        updateDeal(this.dealForm.id, {
          title: this.dealForm.title,
          value: parseFloat(this.dealForm.value) || 0,
          step_id: this.dealForm.step_id,
          user_id: this.dealForm.user_id
        })
          .then(() => {
            this.$toast.success({
              title: "Sucesso",
              message: "Negócio atualizado com sucesso."
            });
            this.showDealModal = false;
            this.loadActivePipeline();
          })
          .catch((err) => {
            console.error(err);
            this.$toast.error({
              title: "Erro",
              message: "Não foi possível salvar o negócio."
            });
          });
      } else {
        // Create flow
        if (this.createContactMode) {
          // Validate new contact form
          if (!this.newContactForm.first_name.trim()) {
            this.$toast.error({
              title: "Validação de Contato",
              message: "O nome do contato é obrigatório."
            });
            return;
          }
          if (!this.newContactForm.email.trim()) {
            this.$toast.error({
              title: "Validação de Contato",
              message: "O e-mail do contato é obrigatório."
            });
            return;
          }
          
          // Auto-fill title if empty
          if (!this.dealForm.title.trim()) {
            this.dealForm.title = `Negócio - ${this.newContactForm.first_name} ${this.newContactForm.last_name || ''}`.trim();
          }

          // Create contact first, then create deal
          createContact(this.newContactForm)
            .then((createdContact) => {
              const contactId = createdContact.id || (createdContact.contact ? createdContact.contact.id : null);
              if (!contactId) {
                throw new Error("Não foi possível capturar o ID do contato criado.");
              }
              this.executeCreateDeal(contactId);
            })
            .catch((err) => {
              console.error(err);
              this.$toast.error({
                title: "Erro de Contato",
                message: "Não foi possível cadastrar o contato. Certifique-se de que o e-mail não seja duplicado."
              });
            });
        } else {
          // Standard selection mode
          if (!this.dealForm.contact_id) {
            this.$toast.error({
              title: "Validação",
              message: "Selecione um contato existente ou cadastre um novo."
            });
            return;
          }
          if (!this.dealForm.title.trim()) {
            this.$toast.error({
              title: "Validação",
              message: "O título do negócio é obrigatório."
            });
            return;
          }
          this.executeCreateDeal(this.dealForm.contact_id);
        }
      }
    },
    executeCreateDeal(contactId) {
      createDeal({
        contact_id: contactId,
        step_id: this.dealForm.step_id,
        title: this.dealForm.title,
        value: parseFloat(this.dealForm.value) || 0,
        user_id: this.dealForm.user_id
      })
        .then(() => {
          this.$toast.success({
            title: "Sucesso",
            message: "Negócio adicionado com sucesso."
          });
          this.showDealModal = false;
          this.loadActivePipeline();
        })
        .catch((err) => {
          console.error(err);
          this.$toast.error({
            title: "Erro",
            message: "Não foi possível criar o negócio."
          });
        });
    },
    deleteDealConfirm(deal, step) {
      if (!confirm(`Tem certeza que deseja excluir o negócio "${deal.title}"?`)) {
        return;
      }
      deleteDeal(deal.id)
        .then(() => {
          this.$toast.success({
            title: "Sucesso",
            message: "Negócio excluído."
          });
          step.deals = step.deals.filter(d => d.id !== deal.id);
          step.total_deals = (step.total_deals || 0) - 1;
          step.summed_value = parseFloat(step.summed_value) - parseFloat(deal.value);
        })
        .catch((err) => {
          console.error(err);
          this.$toast.error({
            title: "Erro",
            message: "Não foi possível excluir o negócio."
          });
        });
    },

    // ----------------------------------------------------
    // Drag & Drop Handling (HTML5 Native)
    // ----------------------------------------------------
    onDragStart(deal, step) {
      this.draggedDeal = deal;
      this.draggedFromStep = step;
    },
    onDragEnd() {
      this.draggedDeal = null;
      this.draggedFromStep = null;
      this.dragOverStepId = null;
    },
    onDragEnter(step) {
      this.dragOverStepId = step.id;
    },
    onDragLeave(step) {
      if (this.dragOverStepId === step.id) {
        this.dragOverStepId = null;
      }
    },
    onDrop(targetStep) {
      if (!this.draggedDeal || !this.draggedFromStep || this.draggedFromStep.id === targetStep.id) {
        return;
      }

      const deal = this.draggedDeal;
      const sourceStep = this.draggedFromStep;

      // Optimistic UI updates
      sourceStep.deals = sourceStep.deals.filter(d => d.id !== deal.id);
      sourceStep.total_deals = (sourceStep.total_deals || 0) - 1;
      sourceStep.summed_value = parseFloat(sourceStep.summed_value) - parseFloat(deal.value);

      targetStep.deals.push(deal);
      targetStep.total_deals = (targetStep.total_deals || 0) + 1;
      targetStep.summed_value = parseFloat(targetStep.summed_value) + parseFloat(deal.value);

      this.dragOverStepId = null;

      // Persist in Backend
      updateDeal(deal.id, { step_id: targetStep.id })
        .then((updatedDeal) => {
          this.$toast.success({
            title: "Movido com sucesso",
            message: `"${deal.title}" movido para "${targetStep.name}"`
          });
          deal.updated_at = updatedDeal.updated_at;
        })
        .catch((err) => {
          console.error(err);
          this.$toast.error({
            title: "Erro ao salvar",
            message: "Ocorreu um erro ao mover o negócio no servidor. Revertendo..."
          });
          
          // Revert optimistic UI
          targetStep.deals = targetStep.deals.filter(d => d.id !== deal.id);
          targetStep.total_deals = (targetStep.total_deals || 0) - 1;
          targetStep.summed_value = parseFloat(targetStep.summed_value) - parseFloat(deal.value);

          sourceStep.deals.push(deal);
          sourceStep.total_deals = (sourceStep.total_deals || 0) + 1;
          sourceStep.summed_value = parseFloat(sourceStep.summed_value) + parseFloat(deal.value);
        });
    },

    handleStepScroll(event, step) {
      const target = event.target;
      if (target.scrollHeight - target.scrollTop <= target.clientHeight + 50) {
        this.loadNextPageForStep(step);
      }
    },
    loadNextPageForStep(step) {
      if (step.loading || !step.has_more) return;

      step.loading = true;
      const nextPage = (step.page || 1) + 1;

      getStepDeals(this.activePipeline.id, step.id, nextPage)
        .then((response) => {
          if (response && response.deals) {
            step.deals = [...step.deals, ...response.deals];
            step.page = response.page;
            step.has_more = response.has_more;
          }
        })
        .catch((err) => {
          console.error("Erro ao carregar mais negócios:", err);
          this.$toast.error({
            title: "Erro",
            message: "Não foi possível carregar mais negócios."
          });
        })
        .finally(() => {
          step.loading = false;
        });
    },

    // Helper functions
    formatMoney(val) {
      if (val === undefined || val === null) return "0,00";
      return parseFloat(val).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.crm-container {
  --color-primary-purple: #7c3aed;
  padding: 0;
  font-family: 'Inter', sans-serif;
  color: var(--color-brand-black);
}

// Visual alignment with the current 123Mail design system.
.crm-header { border: 1px solid #e7e9f0; box-shadow: 0 1px 3px rgba(16,24,40,.04); }
.deal-card .deal-value { color: #6d28d9; }
.deal-card .deal-assignee-badge { color: #6d28d9; background: #f6f1ff; border-color: #e3d8fa; }
.deal-card .deal-assignee-badge i { color: #6d28d9 !important; }
.step-column { border-color: #e7e9f0; background: #f9fafc; }

.crm-header {
  background: var(--color-white);
  padding: var(--spacing-l) var(--spacing-xl);
  border-radius: var(--border-radius-l);
  box-shadow: var(--drop-shadow-12);
  margin-bottom: var(--spacing-2-xl);

  .crm-header-icon {
    font-size: 2.2rem;
    color: var(--color-primary-purple);
    margin-right: var(--spacing-l);
    background: rgba(71, 38, 90, 0.1);
    padding: var(--spacing-m);
    border-radius: var(--border-radius-m);
  }

  .crm-title {
    font-size: var(--text-h3-font-size);
    font-weight: var(--text-font-weight-bold);
    margin: 0 0 4px 0;
  }

  .crm-subtitle {
    font-size: var(--text-s12-font-size);
    color: var(--color-grey-5);
    margin: 0;
  }
}

.select-wrapper {
  position: relative;
  
  .pipeline-select {
    appearance: none;
    background: var(--color-grey-0);
    border: 1px solid var(--color-grey-2);
    padding: 10px 40px 10px 16px;
    border-radius: var(--border-radius-s);
    font-size: var(--text-s14-font-size);
    font-weight: var(--text-font-weight-medium);
    cursor: pointer;
    transition: 0.3s;

    &:focus {
      outline: none;
      border-color: var(--color-primary-purple);
      box-shadow: 0 0 0 2px rgba(71, 38, 90, 0.15);
    }
  }

  .select-chevron {
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    color: var(--color-grey-4);
    font-size: 0.8rem;
  }
}

// Kanban Grid Layout
.kanban-board {
  overflow-x: auto;
  padding-bottom: var(--spacing-l);

  .steps-wrapper {
    display: flex;
    gap: var(--spacing-xl);
    align-items: flex-start;
    min-height: calc(100vh - 300px);
  }
}

.step-column {
  flex: 0 0 290px;
  background: #ffffff;
  backdrop-filter: blur(4px);
  border: 1px solid rgba(222, 224, 227, 0.5);
  border-radius: var(--border-radius-m);
  padding: var(--spacing-l);
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 270px);
  transition: all 0.3s ease;

  &.drag-over {
    border: 2px dashed var(--color-primary-purple);
    background: rgba(71, 38, 90, 0.05);
    transform: scale(1.01);
  }
}

.step-header {
  margin-bottom: var(--spacing-l);
  border-bottom: 2px solid rgba(222, 224, 227, 0.4);
  padding-bottom: var(--spacing-m);

  .step-meta {
    .step-title {
      font-size: var(--text-h6-font-size);
      font-weight: var(--text-font-weight-bold);
      color: var(--color-primary-black);
      cursor: pointer;
    }

    .step-name-input {
      border: 1px solid var(--color-primary-purple);
      border-radius: var(--border-radius-xs);
      padding: 4px 8px;
      font-size: var(--text-s14-font-size);
      width: 140px;
      &:focus {
        outline: none;
      }
    }
  }

  .step-count {
    font-size: var(--text-s10-font-size);
    color: var(--color-grey-5);
    font-weight: var(--text-font-weight-medium);
    margin-top: 2px;
  }

  .step-total {
    font-size: 1.15rem;
    font-weight: var(--text-font-weight-bold);
    margin-top: var(--spacing-s);
    background: linear-gradient(135deg, var(--color-primary-purple), var(--color-blue));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
}

.step-cards {
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-m);
  margin-bottom: var(--spacing-l);
  padding-right: 4px;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: var(--color-grey-3);
    border-radius: 4px;
  }
}

.step-loading-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-m);
  gap: var(--spacing-s);
  color: var(--color-grey-5);
  font-size: 0.9rem;
}

// Premium Deal Card Design
.deal-card {
  background: var(--color-white);
  border: 1px solid var(--color-grey-1);
  border-radius: var(--border-radius-s);
  padding: var(--spacing-m) var(--spacing-l);
  box-shadow: var(--drop-shadow-12);
  cursor: grab;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: translateY(-4px);
    box-shadow: var(--drop-shadow-16);
    border-color: rgba(71, 38, 90, 0.3);
  }

  &:active {
    cursor: grabbing;
  }

  .deal-card-header {
    margin-bottom: var(--spacing-s);
  }

  .deal-card-title {
    font-size: var(--text-s14-font-size);
    font-weight: var(--text-font-weight-bold);
    color: var(--color-primary-black);
    margin: 0;
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 170px;
  }

  .deal-contact-info {
    font-size: var(--text-s10-font-size);
    color: var(--color-grey-6);
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: var(--spacing-m);
    padding-top: var(--spacing-xs);
    border-top: 1px dashed var(--color-grey-1);

    .contact-name {
      font-weight: var(--text-font-weight-medium);
      color: var(--color-brand-black);
    }

    .contact-detail {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .contact-icon {
      color: var(--color-grey-4);
      margin-right: 4px;
      width: 10px;
    }
  }

  .deal-value {
    font-size: var(--text-s14-font-size);
    font-weight: var(--text-font-weight-bold);
    color: #7d0707;
  }

  .deal-assignee-badge {
    background: rgba(125, 7, 7, 0.05);
    color: #7d0707;
    padding: 3px 8px;
    border-radius: 6px;
    font-size: 0.8rem;
    font-weight: 750;
    max-width: 130px;
    border: 1px solid rgba(125, 7, 7, 0.08);

    .assignee-name {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}

.step-empty-state {
  border: 1px dashed var(--color-grey-3);
  border-radius: var(--border-radius-s);
  padding: var(--spacing-xl) var(--spacing-m);
  text-align: center;
  font-size: var(--text-s10-font-size);
  color: var(--color-grey-4);
  line-height: 1.4;
}

.step-add-card-btn {
  width: 100%;
  background: var(--color-white);
  border: 1px dashed var(--color-grey-3);
  color: var(--color-primary-purple);
  padding: 10px;
  border-radius: var(--border-radius-s);
  font-size: var(--text-s12-font-size);
  font-weight: var(--text-font-weight-bold);
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: var(--spacing-s);
  transition: 0.3s;

  &:hover {
    background: var(--color-primary-purple);
    color: var(--color-white);
    border-color: var(--color-primary-purple);
  }
}

// Add Step Column Column
.add-step-column {
  flex: 0 0 240px;
  
  .add-step-btn {
    width: 100%;
    background: rgba(247, 247, 248, 0.4);
    border: 1px dashed var(--color-grey-3);
    color: var(--color-grey-5);
    padding: var(--spacing-l);
    border-radius: var(--border-radius-m);
    font-weight: var(--text-font-weight-bold);
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: var(--spacing-s);
    transition: 0.3s;

    &:hover {
      background: var(--color-white);
      color: var(--color-primary-purple);
      border-color: var(--color-primary-purple);
      box-shadow: var(--drop-shadow-12);
    }
  }

  .add-step-form {
    background: var(--color-white);
    padding: var(--spacing-l);
    border-radius: var(--border-radius-m);
    box-shadow: var(--drop-shadow-12);
    border: 1px solid var(--color-grey-2);

    .new-step-input {
      width: 100%;
      border: 1px solid var(--color-grey-3);
      padding: 8px 12px;
      border-radius: var(--border-radius-s);
      font-size: var(--text-s14-font-size);
      &:focus {
        outline: none;
        border-color: var(--color-primary-purple);
      }
    }
  }
}

// Empty State Styling
.crm-empty-state {
  text-align: center;
  background: var(--color-white);
  padding: 80px 40px;
  border-radius: var(--border-radius-l);
  box-shadow: var(--drop-shadow-12);
  max-width: 600px;
  margin: 40px auto;

  .empty-icon-wrapper {
    background: rgba(71, 38, 90, 0.08);
    width: 90px;
    height: 90px;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0 auto 24px auto;
  }

  .empty-icon {
    font-size: 2.8rem;
    color: var(--color-primary-purple);
  }

  h2 {
    font-size: var(--text-h4-font-size);
    font-weight: var(--text-font-weight-bold);
    margin-bottom: var(--spacing-s);
  }

  p {
    color: var(--color-grey-5);
    font-size: var(--text-s14-font-size);
    max-width: 400px;
    margin: 0 auto;
    line-height: 1.5;
  }
}

// Modal Overlays with Frosted Glass Efx
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(18, 20, 22, 0.55);
  backdrop-filter: blur(8px);
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
}

.modal-card {
  background: var(--color-white);
  border-radius: var(--border-radius-l);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  width: 500px;
  max-width: 90%;
  overflow: hidden;
  animation: modalReveal 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  .modal-header {
    background: var(--color-grey-0);
    padding: var(--spacing-l) var(--spacing-xl);
    border-bottom: 1px solid var(--color-grey-2);
    display: flex;
    justify-content: space-between;
    align-items: center;

    h3 {
      font-size: var(--text-h5-font-size);
      font-weight: var(--text-font-weight-bold);
      margin: 0;
    }

    .modal-close-btn {
      background: none;
      border: none;
      font-size: 1.8rem;
      color: var(--color-grey-4);
      cursor: pointer;
      line-height: 1;
      padding: 0;
      &:hover {
        color: var(--color-red);
      }
    }
  }

  .modal-body {
    padding: var(--spacing-xl);
  }

  .modal-hint {
    font-size: var(--text-s10-font-size);
    color: var(--color-grey-5);
    background: rgba(71, 38, 90, 0.05);
    padding: var(--spacing-m);
    border-radius: var(--border-radius-s);
    border-left: 3px solid var(--color-primary-purple);
    margin-top: var(--spacing-xl);
    line-height: 1.4;
  }

  .modal-footer {
    padding: var(--spacing-l) var(--spacing-xl);
    background: var(--color-grey-0);
    border-top: 1px solid var(--color-grey-2);
  }
}

// Form Elements
.form-group {
  margin-bottom: var(--spacing-xl);

  label {
    display: block;
    font-size: var(--text-s12-font-size);
    font-weight: var(--text-font-weight-bold);
    color: var(--color-brand-black);
    margin-bottom: var(--spacing-s);
  }

  .form-control {
    width: 100%;
    border: 1px solid var(--color-grey-3);
    border-radius: var(--border-radius-s);
    padding: 10px 14px;
    font-size: var(--text-s14-font-size);
    transition: 0.3s;

    &:focus {
      outline: none;
      border-color: var(--color-primary-purple);
      box-shadow: 0 0 0 2px rgba(71, 38, 90, 0.1);
    }
  }

  .search-input-wrapper {
    position: relative;
    .search-icon {
      position: absolute;
      right: 14px;
      top: 50%;
      transform: translateY(-50%);
      color: var(--color-grey-4);
    }
  }
}

// Autocomplete list
.autocomplete-list {
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  background: var(--color-white);
  border: 1px solid var(--color-grey-2);
  border-top: none;
  border-radius: 0 0 var(--border-radius-s) var(--border-radius-s);
  box-shadow: var(--drop-shadow-16);
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 180px;
  overflow-y: auto;
  z-index: 10;

  li {
    padding: 10px 14px;
    font-size: var(--text-s12-font-size);
    cursor: pointer;
    border-bottom: 1px solid var(--color-grey-1);
    transition: 0.2s;

    &:hover {
      background: rgba(71, 38, 90, 0.05);
      color: var(--color-primary-purple);
    }
  }
}

.selected-contact-pill {
  background: rgba(84, 194, 71, 0.08);
  border: 1px solid rgba(84, 194, 71, 0.2);
  padding: 10px 14px;
  border-radius: var(--border-radius-s);
  font-size: var(--text-s12-font-size);
  margin-top: var(--spacing-m);
}

// Custom Buttons Design
.btn {
  border: none;
  padding: 10px 18px;
  border-radius: var(--border-radius-s);
  font-weight: var(--text-font-weight-bold);
  font-size: var(--text-s14-font-size);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-s);
  transition: all 0.25s ease;

  &.small {
    padding: 6px 12px;
    font-size: var(--text-s12-font-size);
  }

  &.btn-block {
    display: flex;
    justify-content: center;
    width: 100%;
  }

  &.btn-primary {
    background: var(--color-primary-purple);
    color: var(--color-white);
    &:hover {
      background: #5c0505;
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(125, 7, 7, 0.25);
    }
    &:active {
      transform: translateY(0);
    }
  }

  &.btn-secondary {
    background: var(--color-grey-1);
    color: var(--color-brand-black);
    border: 1px solid var(--color-grey-2);
    &:hover {
      background: var(--color-grey-2);
      transform: translateY(-2px);
    }
  }

  &.btn-danger {
    background: var(--color-red, #e74c3c);
    color: var(--color-white);
    border: 1px solid var(--color-red, #e74c3c);
    &:hover {
      background: #c0392b;
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(231, 76, 60, 0.25);
    }
  }
}

// Icon Action Buttons
.btn-icon {
  background: none;
  border: none;
  cursor: pointer;
  padding: var(--spacing-xs);
  color: var(--color-grey-4);
  transition: 0.2s;

  &:hover {
    color: var(--color-primary-purple);
  }
}

.btn-card-action {
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  font-size: 0.8rem;
  color: var(--color-grey-4);
  opacity: 0;
  transition: 0.2s;

  .deal-card:hover & {
    opacity: 1;
  }

  &:hover {
    transform: scale(1.15);
  }
}

// Helper display styles
.hover-show {
  opacity: 0;
  transition: 0.2s;
}
.step-header:hover .hover-show {
  opacity: 1;
}

.text-red {
  color: var(--color-red) !important;
}
.text-green {
  color: var(--color-green) !important;
}
.text-purple {
  color: var(--color-primary-purple) !important;
}
.relative {
  position: relative;
}
.gap-s {
  gap: var(--spacing-s);
}
.gap-m {
  gap: var(--spacing-l);
}
.margin-top-s {
  margin-top: var(--spacing-s);
}
.margin-top-m {
  margin-top: var(--spacing-l);
}
.margin-right-s {
  margin-right: var(--spacing-s);
}
.btn-block {
  width: 100%;
}

@keyframes modalReveal {
  from {
    opacity: 0;
    transform: scale(0.92) translateY(15px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@media (max-width: 768px) {
  .crm-header {
    flex-direction: column;
    align-items: flex-start;
  }
  .margin-top-mobile {
    margin-top: var(--spacing-m);
    width: 100%;
  }
  .pipeline-select {
    width: 100%;
  }
}

// Deal Details Modal Styles (Premium HubSpot Redesign)
.deal-details-modal {
  max-width: 960px;
  width: 95%;
  border-radius: 18px;
  background: #ffffff;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.deal-status-badge {
  width: 48px;
  height: 48px;
  background: rgba(125, 7, 7, 0.06);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #7d0707;
  font-size: 1.6rem;
  border: 1.5px solid rgba(125, 7, 7, 0.12);
  flex-shrink: 0;
}

.modal-title {
  font-size: 1.6rem;
  font-weight: 700;
  color: #1a202c;
  margin: 0;
}

.modal-subtitle {
  margin: 4px 0 0 0;
  font-size: 1rem;
  color: #718096;
}

.modal-close-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  color: #4a5568;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 1rem;

  &:hover {
    background: #edf2f7;
    color: #1a202c;
    transform: rotate(90deg);
  }
}

.deal-details-body-grid {
  display: grid;
  grid-template-columns: 1fr;
  padding: 0;
  background: #f7fafc;

  @media (min-width: 768px) {
    grid-template-columns: 1.4fr 1fr;
  }
}

// Left Timeline Panel
.deal-timeline-panel {
  background: #ffffff;
  padding: 30px;
  border-right: 1px solid #e2e8f0;
}

.section-header {
  margin-bottom: 8px;
}

.badge-count {
  background: #edf2f7;
  color: #4a5568;
  padding: 4px 12px;
  font-size: 0.9rem;
  font-weight: 700;
  border-radius: 99px;
  border: 1px solid #e2e8f0;
}

.section-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #2d3748;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;

  i {
    color: #7d0707;
  }
}

// Comments Feed styling
.comments-feed-container {
  width: 100%;
  min-height: 250px;
  max-height: 380px;
  overflow-y: auto;
  padding-right: 8px;
  margin-top: 12px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #cbd5e0;
    border-radius: 3px;
  }
}

.empty-feed-state {
  text-align: center;
  padding: 50px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  .empty-icon-circle {
    width: 68px;
    height: 68px;
    border-radius: 50%;
    background: #f7fafc;
    border: 2px dashed #cbd5e0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #a0aec0;
    font-size: 2rem;
    margin-bottom: 16px;
  }

  h5 {
    font-size: 1.15rem;
    font-weight: 700;
    color: #4a5568;
    margin: 0 0 8px 0;
  }

  p {
    font-size: 1.05rem;
    color: #718096;
    max-width: 380px;
    margin: 0;
    line-height: 1.5;
  }
}

// Vertical Timeline Feed
.timeline-feed {
  display: flex;
  flex-direction: column;
  position: relative;
  padding-left: 20px;
  border-left: 2px solid #e2e8f0;
  margin-left: 10px;
  gap: 20px;
  padding-top: 5px;
  padding-bottom: 5px;
}

.timeline-item {
  position: relative;
}

.timeline-badge {
  position: absolute;
  left: -31px;
  top: 4px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #ffffff;
  border: 2px solid #7d0707;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #7d0707;
  font-size: 0.65rem;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
}

.timeline-content-card {
  background: #f8fafc;
  border: 1px solid #edf2f7;
  border-radius: 12px;
  padding: 16px 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.01);
  transition: all 0.2s ease;

  &:hover {
    background: #ffffff;
    border-color: rgba(125, 7, 7, 0.2);
    box-shadow: 0 4px 12px rgba(125, 7, 7, 0.04);
  }
}

.timeline-card-header {
  border-bottom: 1px dashed #e2e8f0;
  padding-bottom: 8px;
  margin-bottom: 8px;
}

.comment-author {
  font-size: 1.05rem;
  font-weight: 700;
  color: #2d3748;
}

.comment-date {
  font-size: 0.9rem;
  color: #a0aec0;
}

.comment-text-content {
  font-size: 1.1rem;
  color: #4a5568;
  line-height: 1.55;
  white-space: pre-wrap;
}

// Add Note Area
.add-note-box {
  margin-top: 15px;
  border-top: 1px solid #edf2f7;
  padding-top: 20px;
}

.input-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #4a5568;
  margin: 0 0 4px 0;
}

.textarea-wrapper {
  position: relative;
}

.comment-textarea {
  width: 100%;
  resize: none;
  font-size: 1.1rem;
  padding: 14px 16px 14px 42px;
  border-radius: 10px;
  border: 1.5px solid #e2e8f0;
  background: #f8fafc;
  transition: all 0.2s ease;
  line-height: 1.55;

  &:focus {
    background: #ffffff;
    border-color: #7d0707;
    box-shadow: 0 0 0 3px rgba(125, 7, 7, 0.08);
    outline: none;
  }
}

.textarea-icon {
  position: absolute;
  left: 16px;
  top: 17px;
  color: #a0aec0;
  font-size: 1.05rem;
}

.btn-add-comment {
  padding: 11px 22px;
  font-size: 1.05rem;
  font-weight: 700;
  border-radius: 8px;
  background: #7d0707;
  color: #ffffff;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 10px rgba(125, 7, 7, 0.15);

  &:hover {
    background: #5c0505;
    transform: translateY(-1px);
    box-shadow: 0 6px 14px rgba(125, 7, 7, 0.22);
  }

  &:disabled {
    background: #cbd5e0;
    color: #a0aec0;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
}

// Right Sidebar Panel
.deal-info-sidebar {
  padding: 30px;
  background: #f8fafc;
  border-left: 1px solid #edf2f7;
}

.sidebar-value-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 22px;
  text-align: center;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.01);
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 8px;
}

.sidebar-label {
  font-size: 0.85rem;
  font-weight: 800;
  color: #a0aec0;
  letter-spacing: 1px;
  text-transform: uppercase;
}

.sidebar-value {
  font-size: 2rem;
  font-weight: 850;
  letter-spacing: -0.5px;
}

.sidebar-contact-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 24px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.01);
  width: 100%;
  margin-top:16px;

  .section-title {
    font-size: 1.15rem;
    margin-bottom: 16px;
  }
}

.contact-details-list {
  background: #f8fafc;
  border: 1px solid #edf2f7;
  border-radius: 10px;
  padding: 14px 16px;
}

.contact-detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px dashed #edf2f7;
  font-size: 1.05rem;

  &:last-child {
    border-bottom: none;
  }
}

.row-label {
  color: #718096;
  font-weight: 550;
}

.row-value {
  color: #2d3748;
  word-break: break-all;
}

.select-all {
  user-select: all;
}

// Channels Action Buttons
.channels-action-grid {
  margin-top: 20px;
}

.action-channel-btn {
  padding: 14px 18px;
  font-size: 1.05rem;
  font-weight: 700;
  border-radius: 10px;
  text-decoration: none;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.25s ease;
  cursor: pointer;
  border: none;
  width: 100%;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.02);

  &.whatsapp-action-btn {
    background: linear-gradient(135deg, #1fa34c, #25d366);
    color: #ffffff !important;

    &:hover {
      background: linear-gradient(135deg, #16803b, #1fa34c);
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba(37, 211, 102, 0.25);
    }
  }

  &.email-action-btn {
    background: linear-gradient(135deg, #0056b3, #007bff);
    color: #ffffff !important;

    &:hover {
      background: linear-gradient(135deg, #004085, #0056b3);
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba(0, 123, 255, 0.25);
    }
  }

  &.disabled {
    background: #e2e8f0;
    color: #a0aec0 !important;
    cursor: not-allowed;
    opacity: 0.8;
  }
}

.btn-secondary-outline {
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 700;
  border-radius: 8px;
  background: #ffffff;
  color: #4a5568;
  border: 1.5px solid #cbd5e0;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f7fafc;
    border-color: #a0aec0;
    color: #2d3748;
  }
}

.crm-container .deal-card .deal-value { color: #6d28d9; }
.crm-container .deal-card .deal-assignee-badge { color: #6d28d9; background: #f6f1ff; border-color: #e3d8fa; }
.crm-container .deal-card .deal-assignee-badge i { color: #6d28d9 !important; }
.crm-container .btn.btn-primary, .crm-container .btn-add-comment { background: #7c3aed; box-shadow: 0 4px 10px rgba(124,58,237,.18); }
.crm-container .btn.btn-primary:hover, .crm-container .btn-add-comment:hover { background: #6d28d9; box-shadow: 0 6px 14px rgba(124,58,237,.24); }
.crm-container .deal-status-badge, .crm-container .section-title i, .crm-container .timeline-badge { color: #7c3aed; border-color: #7c3aed; }
.crm-container .deal-status-badge { background: #f6f1ff; }
</style>
